/**
 * Kebab tracker — challenge window, counts, and media URL helpers.
 * Photo bytes stay on the API; this package stays DOM-free for a later Expo client.
 */

import type { UserRole } from "./types.js";
import { API_PREFIX } from "./api.js";

export const KEBAB_DEFAULT_START = "2026-02-10";
export const KEBAB_DEFAULT_END = "2027-02-10";

export const KEBAB_PREVIEW_LIMIT = 6;
export const KEBAB_MAX_PHOTO_BYTES = 25 * 1024 * 1024;
export const KEBAB_MAX_AVATAR_BYTES = 2 * 1024 * 1024;

export type KebabMediaKind = "uploads" | "avatars";
export type KebabChallengePhase = "before" | "during" | "after";

export interface KebabCountdown {
  phase: KebabChallengePhase;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export interface KebabEntry {
  id: number;
  contestantId: number;
  contestantName: string;
  /** Null when the caller is not a contestant or admin. */
  imageUrl: string | null;
  createdAt: string;
  notes: string | null;
  location: string | null;
  price: number | null;
}

export interface KebabContestant {
  id: number;
  name: string;
  avatarUrl: string | null;
  count: number;
  totalPrice: number;
  isMe: boolean;
  preview: KebabEntry[];
}

export interface KebabBoard {
  startDate: string;
  endDate: string;
  meContestantId: number | null;
  /** Photos and avatars are only sent when this is true. */
  canViewPhotos: boolean;
  contestants: KebabContestant[];
}

/** Contestants (linked site accounts) and admins may see kebab photos. */
export function canViewKebabPhotos(
  role: UserRole | null | undefined,
  isContestant: boolean,
): boolean {
  return role === "admin" || isContestant;
}

export function kebabMediaUrl(kind: KebabMediaKind, filename: string): string {
  return `${API_PREFIX}/games/kebab/media/${kind}/${encodeURIComponent(filename)}`;
}

/** Visible leaderboard count: stored offset hides kebabs logged before tracking. */
export function kebabVisibleCount(total: number, offset: number): number {
  return Math.max(0, total - offset);
}

/**
 * Before the start date every entry counts (warm-up photos). Once the challenge
 * is running, only rows inside `[startDate, endDate]` count toward the total.
 */
export function kebabCountsTowardTotal(
  createdAt: string,
  startDate: string,
  endDate: string,
  now: Date,
): boolean {
  if (!kebabChallengeHasStarted(now, startDate)) return true;
  const day = createdAt.slice(0, 10);
  return day >= startDate && day <= endDate;
}

export function kebabChallengeHasStarted(now: Date, startDate: string): boolean {
  return now.getTime() >= Date.parse(`${startDate}T00:00:00`);
}

export function kebabCountdown(
  nowMs: number,
  startDate: string,
  endDate: string,
): KebabCountdown {
  const start = Date.parse(`${startDate}T00:00:00`);
  const end = Date.parse(`${endDate}T23:59:59`);

  let phase: KebabChallengePhase;
  let target: number;
  if (nowMs < start) {
    phase = "before";
    target = start;
  } else if (nowMs <= end) {
    phase = "during";
    target = end;
  } else {
    phase = "after";
    target = end;
  }

  const diff = Math.max(0, target - nowMs);
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  const minutes = Math.floor((diff % 3_600_000) / 60_000);
  const seconds = Math.floor((diff % 60_000) / 1000);

  return { phase, days, hours, minutes, seconds };
}

export function formatKebabPrice(price: number): string {
  return `${price.toFixed(2).replace(".", ",")} zł`;
}

/** 1 kebab, 2–4 kebaby, 5+ kebabów. */
export function pluralKebabs(count: number): string {
  const lastTwo = count % 100;
  const last = count % 10;
  if (count === 1) return "1 kebab";
  if (last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)) {
    return `${count} kebaby`;
  }
  return `${count} kebabów`;
}

export const KEBAB_CHART_COLORS = [
  "#4caf50",
  "#26a69a",
  "#5ba3f5",
  "#ff8c42",
  "#ec407a",
  "#9ccc65",
] as const;

export const KEBAB_CHART_SIZE = {
  width: 640,
  height: 280,
  padTop: 28,
  padRight: 16,
  padBottom: 44,
  padLeft: 36,
} as const;

export interface KebabChartPoint {
  day: string;
  count: number;
}

export interface KebabChartSeries {
  contestantId: number;
  name: string;
  color: string;
  points: KebabChartPoint[];
}

export interface KebabBar {
  id: number;
  name: string;
  count: number;
  color: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export function kebabChartColor(index: number): string {
  const color = KEBAB_CHART_COLORS[index % KEBAB_CHART_COLORS.length];
  return color ?? "#4caf50";
}

export function kebabIsoDay(value: string): string {
  return value.slice(0, 10);
}

/** Calendar day in the viewer's local zone, `YYYY-MM-DD`. */
export function kebabLocalIsoDay(now: Date): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Whole days from `fromDay` to `toDay` using UTC midnight so zones cannot shift the count. */
export function kebabDayDiff(fromDay: string, toDay: string): number {
  const from = Date.parse(`${fromDay}T00:00:00Z`);
  const to = Date.parse(`${toDay}T00:00:00Z`);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return 0;
  return Math.round((to - from) / 86_400_000);
}

export function formatKebabChartDay(day: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(day);
  if (!match) return day;
  return `${match[3]}.${match[2]}`;
}

export function kebabChartYTicks(max: number): number[] {
  const top = Math.max(1, Math.ceil(max));
  if (top <= 4) {
    return Array.from({ length: top + 1 }, (_, index) => index);
  }
  const step = Math.ceil(top / 4);
  const ticks = [0];
  for (let value = step; value < top; value += step) ticks.push(value);
  if (ticks.at(-1) !== top) ticks.push(top);
  return ticks;
}

export function kebabBarPlot(
  contestants: Array<{ id: number; name: string; count: number }>,
): { max: number; plotHeight: number; bars: KebabBar[] } {
  const size = KEBAB_CHART_SIZE;
  const plotWidth = size.width - size.padLeft - size.padRight;
  const plotHeight = size.height - size.padTop - size.padBottom;
  const max = Math.max(1, ...contestants.map((contestant) => contestant.count));
  const slot = contestants.length === 0 ? plotWidth : plotWidth / contestants.length;
  const width = Math.min(48, slot * 0.46);

  const bars = contestants.map((contestant, index) => {
    const height = (contestant.count / max) * plotHeight;
    return {
      id: contestant.id,
      name: contestant.name,
      count: contestant.count,
      color: kebabChartColor(index),
      x: size.padLeft + index * slot + (slot - width) / 2,
      y: size.padTop + plotHeight - height,
      width,
      height,
    };
  });

  return { max, plotHeight, bars };
}

/**
 * Daily cumulative totals that match the leaderboard: in-window (or warm-up) entries,
 * minus the stored count offset which hides the earliest counting kebabs.
 */
export function kebabCumulativeSeries(
  contestants: Array<{ id: number; name: string; count: number }>,
  entries: Array<{ contestantId: number; createdAt: string }>,
  startDate: string,
  endDate: string,
  now: Date,
): KebabChartSeries[] {
  return contestants.map((contestant, index) => {
    const counting = entries
      .filter(
        (entry) =>
          entry.contestantId === contestant.id &&
          kebabCountsTowardTotal(entry.createdAt, startDate, endDate, now),
      )
      .sort((left, right) => left.createdAt.localeCompare(right.createdAt));
    const offset = Math.max(0, counting.length - contestant.count);
    const visible = counting.slice(offset);
    const points: KebabChartPoint[] = [{ day: startDate, count: 0 }];

    visible.forEach((entry, entryIndex) => {
      const day = kebabIsoDay(entry.createdAt);
      const count = entryIndex + 1;
      const last = points.at(-1);
      if (last && last.day === day) last.count = count;
      else points.push({ day, count });
    });

    const today = kebabLocalIsoDay(now);
    const end = today < endDate ? today : endDate;
    const last = points.at(-1);
    if (last && last.day !== end && end >= startDate) {
      points.push({ day: end, count: last.count });
    }

    return {
      contestantId: contestant.id,
      name: contestant.name,
      color: kebabChartColor(index),
      points,
    };
  });
}

export function kebabChartX(
  day: string,
  from: string,
  to: string,
  size = KEBAB_CHART_SIZE,
): number {
  const span = Math.max(1, kebabDayDiff(from, to));
  const plotWidth = size.width - size.padLeft - size.padRight;
  const t = Math.min(1, Math.max(0, kebabDayDiff(from, day) / span));
  return size.padLeft + t * plotWidth;
}

export function kebabChartY(count: number, max: number, size = KEBAB_CHART_SIZE): number {
  const plotHeight = size.height - size.padTop - size.padBottom;
  const top = Math.max(1, max);
  return size.padTop + plotHeight - (count / top) * plotHeight;
}
